import {
	canPlayerMove,
	canPlayerMoveOnExistingLine,
	fillPolygon,
	findSmaller,
	getInitialLines,
	getInitialLeptonStates,
	isOnLine,
} from './GameEngine';
import React, { PureComponent } from 'react';
import partition from 'lodash.partition';
import { describeLeptons } from './Utils';
import { areHitBoxesColliding, areLinesIntersectingHitBox, findLinesIntersectingHitBox, hitBox, trailingHitBox} from './utils/HitBox';

import { Direction, DrawMode, FrenzyDirection, LeptonDirection, LeptonState, FrenzyLine, FrenzyPolygon, FrenzyFilledPolygon, PixelField, Point } from './Model';
import Display from './Display';
import {
	FrenzyColor, FrenzyKeys, HEIGHT,
	TOTAL_AREA,
	WIDTH,
	MAX_X,
	MAX_Y,
} from './FrenzyConstants';
import { KeyListener } from 'react-game-kit';
import Player, { getPlayerHitBox } from './Player';
import Lepton from './Lepton';
import PropTypes from 'prop-types';
import { Level } from './Model';
// import { describeLines, describePolygon, mostBottomRightActualPointForPolygon } from './Utils';
	import { addNonEmptyLines } from './Utils';
import styled from 'react-emotion';
import { buildLinesToJoinPoints, describeLine, describeLines, findIntersectingLineOfSignificance, findIntersectingLineOfSignificanceForHitBox } from './utils/Line';
import { GameState } from './model/GameState';
import { floodFillNextTick } from './model/FloodFill';
import { describePolygons, describePolygon, linesToPolygon, isInsideComplexPolygon, topmostPointForPolygons } from './utils/Polygon';

type GamePanelProps = {
	scale: number;
}

const GamePanel = styled('div')<GamePanelProps>`
	background-color: ${FrenzyColor.BLUE};
	margin: 0 auto;
	position: relative;
	width: ${props => WIDTH * props.scale}px;
	height: ${props => HEIGHT * props.scale}px;
	border: 0;
	padding: 0;
`;

GamePanel.displayName = 'GamePanel';

type Props = {
	scale: number;
	paused: boolean;
	drawMode: DrawMode;
	level: Level;
	onScoreIncrement: (pointsToIncrement:number) => void;
	onLevelTransitionFinished: (pointsToIncrement:number) => void;
	onFinalScoreCalculated: (pointsToIncrement:number) => void;
	onDrawFinished: () => void;
	onPlayerDied: () => boolean; // true if it's now game over
	onLeptonsKilled: (numberOfLeptonsKilled: number) => void;
	onFinalLeptonKilled: () => void;
	// Returns true if the completion percentage is such that the level is done
	onCompletionPercentageChanged: (completionPercentage: number) => boolean;
	requestCounterPauseState: (counterEnabled: boolean) => void;
};

type State = {
	playerX: number;
	playerY: number;
	lines: FrenzyLine[];
	currentDrawingLines: FrenzyLine[];
	currentSegment: FrenzyLine | null;
	currentDirection: FrenzyDirection;
	gameState: GameState,
	fillLines: FrenzyLine[];
	fillSpeed: DrawMode;
	filledPolygons: FrenzyFilledPolygon[];
	newFilledPolygons?: FrenzyFilledPolygon[]; // These are the polys that will exist once the fill animation has run
	filledPercentage: number,
	polygonToFill?: FrenzyFilledPolygon,
	partiallyFilledArea?: PixelField,
	drainAnimationRow: number;
	leptons: LeptonState[];
	dyingLeptons: LeptonState[];
	leptonDeathTick: number;
	finalizingScore: boolean;
}

const INITIAL_PLAYER_X = Math.floor(MAX_X / 2);
const INITIAL_PLAYER_Y = MAX_Y;

const PLAYER_DEATH_ANIMATION_LENGTH_MS = 3000;
const LEPTON_DEATH_ANIMATION_LENGTH_MS = 2000;

const LEPTON_LENGTH = 10;
type PlayerPositionAndDirection = {
	playerX: number;
	playerY: number;
	chosenDirection: FrenzyDirection | null;
}

const BORDER_LINES = getInitialLines();

class GameArea extends PureComponent<Props, State> {
	private keyListener: KeyListener;
	private loopSubscriptionId: number;
	private playerDeathAnimationStartedAt: number;
	private leptonDeathAnimationStartedAt: number;

	static contextTypes = {
		loop: PropTypes.object,
	};

	constructor(props: Props) {
		super(props);

		this.keyListener = new KeyListener();
		this.loopSubscriptionId = 0;
		this.playerDeathAnimationStartedAt = 0;
		this.leptonDeathAnimationStartedAt = 0;

		this.state = {
			playerX: INITIAL_PLAYER_X,
			playerY: INITIAL_PLAYER_Y,
			lines: BORDER_LINES,
			// lines:  [...getInitialLines(),
			// 	// Box from bottom edge
			// 		pointsToLine([119, MAX_Y], [119, 113]),
			// 		pointsToLine([119, 113], [108, 113]),
			// 		pointsToLine([108, 113], [108, MAX_Y]),

			// 		// Box builds on previous
			// 		pointsToLine([108, 113], [108,68]),
			// 		pointsToLine([108,68], [115,68]),
			// 		pointsToLine([115,68], [115,113]),

			// 		// pointsToLine([115,68], [115,48]),
			// 		// pointsToLine([115,48], [107,48]),
			// 		// pointsToLine([107,48], [107,81]),
			// 		// pointsToLine([180, 140], [180, MAX_Y]),
			// ],

			// Useful rectangles for Lepton bounce tests
			// 	lines:  [...getInitialLines(),
			// 		pointsToLine([120, MAX_Y], [120, 140]),
			// 		pointsToLine([120, 140], [140, 140]),
			// 		pointsToLine([140, 140], [140, MAX_Y]),
			// 		pointsToLine([140, MAX_Y], [120, MAX_Y]),
			// 		pointsToLine([80, MAX_Y], [80, 140]),
			// 		pointsToLine([80, 140], [120, 140]),
			// 		pointsToLine([120, 140], [120, MAX_Y]),
			// 		pointsToLine([120, MAX_Y], [80, MAX_Y]),
			// 		pointsToLine([140, 140], [180, 140]),
			// 		pointsToLine([180, 140], [180, MAX_Y]),
			// ],
			currentDrawingLines: [],
			currentSegment: null,
			currentDirection: Direction.UP,
			gameState: GameState.IDLE,
			fillSpeed: DrawMode.NOT_DRAWING,
			fillLines: [],
			filledPolygons: [],
			filledPercentage: 0,
			drainAnimationRow: -1,
			leptons: getInitialLeptonStates(props.level),
			dyingLeptons: [], 
			leptonDeathTick: 0,
			finalizingScore: false,
		}
	}

	componentDidMount() {
		this.loopSubscriptionId = this.context.loop.subscribe(this.update);
		this.keyListener.subscribe([
			this.keyListener.UP,
			this.keyListener.DOWN,
			this.keyListener.LEFT,
			this.keyListener.RIGHT,
			FrenzyKeys.D,
			FrenzyKeys.J,
		]);
	}

	componentWillUnmount() {
		this.context.loop.unsubscribe(this.loopSubscriptionId);
		this.keyListener.unsubscribe();
	}

	isOnLine = (x: number, y: number, lines?: FrenzyLine[]) => {
		return isOnLine(x || this.state.playerX, y || this.state.playerY, lines || this.state.lines);
	}


 static moveOnLine = (allDesiredDirections: FrenzyDirection[], playerX: number, playerY: number, currentDirection: FrenzyDirection, lines: FrenzyLine[], drawMode:DrawMode, depth: number = 0):PlayerPositionAndDirection => {

		if (allDesiredDirections) {
			// console.log(`MoveOnLine @ ${playerX},${playerY} depth ${depth}, desires: ${allDesiredDirections.map(dd => dd.label)}`)

			const moveableDirections = allDesiredDirections.filter(dd => canPlayerMoveOnExistingLine(playerX, playerY, dd, lines));

			if (moveableDirections.length > 0) {
				// console.log(`MoveOnLine @ ${playerX},${playerY} depth ${depth}, des: ${allDesiredDirections.map(dd => dd.label)}, mov: ${moveableDirections.map(md => md.label)}`)

				// If there is a combination of keys down, it's for a reason.
				// Go with the line we're not currently "on".
				let chosenDirection = moveableDirections[0];
	
				if (moveableDirections.length > 1) {
					chosenDirection = moveableDirections.find(md => md !== currentDirection) || currentDirection;
					// console.log(`TIE BREAK was going ${currentDirection.label} but will favour ${chosenDirection.label}`);
				}
	
				if (depth === 0) {
					const { playerX: firstPassX, playerY: firstPassY } = chosenDirection.positionModifier({ playerX, playerY }, drawMode);	
					// console.log(`Recursing with ${firstPassX},${firstPassY} in ${currentDirection.label}`)
					const recursionResult = GameArea.moveOnLine(allDesiredDirections, firstPassX, firstPassY, currentDirection, lines, drawMode, 1);
					if (recursionResult.chosenDirection) {
						return recursionResult;
					} else {
						// couldn't go that way. Return the best we could do:
						const halfMove = {
							chosenDirection,
							playerX: firstPassX,
							playerY: firstPassY
						};
						// console.log(`Recursion no good, returning ${JSON.stringify(halfMove)}`);
						return halfMove;
					}
				}

				return {
					...chosenDirection.positionModifier({ playerX, playerY }, drawMode),
					chosenDirection
				}; 
			} else {
				// console.log(`No moveable directions from here`)
			}
		}
		return {
			chosenDirection: null,
			playerX,
			playerY
		};
	}

	canMove = (direction: FrenzyDirection) => {
		return canPlayerMove(this.state.playerX, this.state.playerY, direction, this.state.lines, this.state.filledPolygons);
	}

	startNewSegment = (direction: FrenzyDirection, endPoint?: Point): FrenzyLine => {
		const { playerX, playerY } = this.state;
		return {
			direction,
			start: [playerX, playerY],
			end: endPoint || [playerX, playerY]
		};
	}

	startDraw = (currentDirection: FrenzyDirection) => {
		if (this.canMove(currentDirection)) {
			const currentSegment = this.startNewSegment(currentDirection);
			const { drawMode } = this.props;
			const { playerX, playerY } = currentDirection.positionModifier( this.state, drawMode );
			this.setState({
				playerX,
				playerY,
				gameState: GameState.DRAWING,
				currentDrawingLines: [],
				currentDirection,
				currentSegment
			});

			return [playerX, playerY];
		}

		return [this.state.playerX, this.state.playerY];
	}

	playerDied = () => {
		const { onPlayerDied } = this.props;
		const gameOver = onPlayerDied();

		if (!gameOver) {
			this.setState({
				playerX: INITIAL_PLAYER_X,
				playerY: INITIAL_PLAYER_Y,
				currentDrawingLines: [],
				currentSegment: null,
				currentDirection: Direction.UP,
				gameState: GameState.PLAYER_DIED,
				fillSpeed: DrawMode.NOT_DRAWING
			});
		} else {
			// All done - do the final tally:
			this.setState({
				gameState: GameState.DRAINING,
				finalizingScore: true,
			})
		}
	}

	updateDraw = (newDirection: FrenzyDirection): [number, number] => {
		const { playerX, playerY, currentDirection, currentDrawingLines } = this.state;
		const { drawMode } = this.props;
		
		if (newDirection.label === currentDirection.oppositeLabel) {
			// Nope. Can't double back. Ignore
			console.log('No backsies!');
			return [playerX, playerY];
		}

		// Update our position 1px, check for intersections...

		const currentSegment = this.state.currentSegment;
		if (!currentSegment) return [playerX, playerY]; // shouldn't happen

		const { playerX: newPlayerX, playerY: newPlayerY } = newDirection.positionModifier( this.state, drawMode);

		if (this.isOnLine(newPlayerX, newPlayerY, currentDrawingLines)) {
			console.log(`Cannot draw over just-drawn line!: ${newPlayerX},${newPlayerY} intersects ${describeLines(currentDrawingLines)}`);
			return [playerX, playerY];
		}

		if (currentDirection === newDirection) {
			currentSegment.end[0] = Math.floor(newPlayerX);
			currentSegment.end[1] = Math.floor(newPlayerY);
			// console.log(`${currentDirection.label} updated currentSegment to ${playerX}, ${playerY} with `, currentSegment);
			this.setState({ playerX: newPlayerX, playerY: newPlayerY, currentSegment });



		} else {
			// console.log(`dir change. Currently at ${this.state.playerX}, ${this.state.playerY}`);
			// Changed direction. Bump the current segment into the currentDrawingLines and
			// start a new segment
			const currentDrawingLines = [...this.state.currentDrawingLines, currentSegment];
			const newSegment = this.startNewSegment(newDirection, [newPlayerX, newPlayerY]);

			this.setState({ currentSegment: newSegment, currentDrawingLines, currentDirection: newDirection });
		}



		return [newPlayerX, newPlayerY];
	}

	finishDraw = () => {
		if (!this.state.currentSegment) return; // shouldn't happen...

		// Bump the current segment into the currentDrawingLines
		const cdls = [...this.state.currentDrawingLines, this.state.currentSegment];

		// console.log(`CDLs at finishDraw: ${describeLines(cdls)}`)

		this.setState({
			gameState: GameState.FILL_REQUIRED,
			currentSegment: null,
			currentDrawingLines: [],
			fillLines: cdls,
			fillSpeed: this.props.drawMode
		});
	}

	drawIsBeingCancelled = (desiredDirection: FrenzyDirection) => {

		// If the draw hasn't yet drawn a pixel, it can be cancelled
		// by moving off along an existing line
		if (this.state.currentDrawingLines === null) {
			// console.log('in a cancellable state');
			const { playerX, playerY } = desiredDirection.positionModifier( this.state, this.props.drawMode);
			if (this.isOnLine(playerX, playerY)) {
				return true;
			}
		}


		return false;
	}

	/** Returns an array of the PRIMARY desired direction, plus
	 * any other directions. This allows a smooth movement when 
	 * following lines for example (see moveOnLine)
	 */
	keyDownDirection = () => {
		let directions: FrenzyDirection[] = [];

		if (this.keyListener.isDown(FrenzyKeys.D)) {
			const { playerX, playerY, lines, currentDrawingLines, currentSegment, currentDirection, filledPolygons} = this.state;
			console.log(`GameArea state dump:`);
			console.log(`Player: ${playerX}, ${playerY}`);
			console.log(`Lines: ${describeLines(lines)}`);
			console.log(`CDLs: ${describeLines(currentDrawingLines)}`);
			console.log(`Current segment: ${currentSegment ? describeLine(currentSegment) : 'none'}`);
			console.log(`Current direction: ${currentDirection.label}`);
			console.log(`Filled polys: ${describePolygons(filledPolygons)}`);
		}

		if (this.keyListener.isDown(FrenzyKeys.J)) {
			const { playerX, playerY, lines, currentDrawingLines, currentSegment, currentDirection, filledPolygons} = this.state;
			// console.log(`GameArea JSON dump:`);
			// console.log(`Filled polys: ${JSON.stringify(filledPolygons)}`);
		}


		if (this.keyListener.isDown(this.keyListener.LEFT)) {
			directions = [Direction.LEFT];
		}
		if (this.keyListener.isDown(this.keyListener.RIGHT)) {
			directions.push(Direction.RIGHT);
		}
		if (this.keyListener.isDown(this.keyListener.UP)) {
			directions.push(Direction.UP);
		}
		if (this.keyListener.isDown(this.keyListener.DOWN)) {
			directions.push(Direction.DOWN);
		}
		return directions;
	}



	fillArea = (fillLines: FrenzyLine[], drawMode: DrawMode, lines: FrenzyLine[], filledPolygons: FrenzyFilledPolygon[]) => {
		const smallerPolygon = findSmaller(fillLines, lines);
		// console.log(`smallerPoly: ${describePolygon(smallerPolygon)}`)
		const filledPolygon = fillPolygon(smallerPolygon, drawMode);
		const newFilledPolygons = [...filledPolygons, filledPolygon];

		// console.log(`newFilledPolygons is now: ${describePolygons(newFilledPolygons)}`);

		// Now that we've filled, we can add the 'fillLines' to the overall finished set
		const newLines = addNonEmptyLines(lines, fillLines);

		// console.log(`fillArea: Lines is now ${describeLines(newLines)}`)
		// console.log(`fillArea: filledPolygons is now ${describePolygons(newFilledPolygons)}`)


		const sumOfFilledAreas = newFilledPolygons.reduce((acc, fp) => acc + fp.area, 0);
		// console.log('sum of filled areas: ', sumOfFilledAreas);
		const filledPercentage = sumOfFilledAreas * 100 / TOTAL_AREA;
		// console.log(`Filled: ${filledPercentage}%`);

		return { newLines, newFilledPolygons, filledPercentage, filledPolygon };	
	}

	animateLeptons = () => {
		const { gameState, currentDrawingLines, currentSegment, leptons, lines, playerX, playerY } = this.state;
		const { level } = this.props;
		let dying = false;

		const newLeptons = leptons.map( lepton => {
			const { x, y } = lepton.direction.positionModifier(lepton,level.leptonSpeed);
			const leptonHitBox = trailingHitBox(x, y, lepton.direction, level.leptonSpeed -1);

			// Check if we're on a line that the player is in the act of drawing.
			// or if we've hit the player directly
			if (gameState === GameState.DRAWING) {
				if (areHitBoxesColliding(leptonHitBox, getPlayerHitBox(playerX, playerY))) {
					console.log(`Lepton at ${x},${y} has DIRECT HIT player at ${playerX}, ${playerY}`);
					dying = true;
					return lepton;
				}
			
				const deathLines = [...currentDrawingLines];
				if (currentSegment) {
					deathLines.push(currentSegment);
				}
				const isLethalLineHit = areLinesIntersectingHitBox(deathLines, leptonHitBox);
				if (isLethalLineHit) {
					console.log(`Lepton at ${x},${y} has HIT ${describeLines(deathLines)}`);
					dying = true;
					return lepton;
				}
			}

			const intersects = findLinesIntersectingHitBox(lines, leptonHitBox);
			if (intersects.length > 0) {
				let intersectingLine = intersects[0];
					if (intersects.length > 1) {
						if (leptonHitBox.boxSize === 1) {
							// console.log(`There were actually ${intersects.length} intersects at ${x}, ${y} - (hitbox ${describeHitBox(leptonHitBox)})`);
							intersectingLine = findIntersectingLineOfSignificance(intersects, {leptonId: lepton.leptonId, x, y, direction: lepton.direction});
						} else {
							// lepton is moving fast and has a hitbox - find the actual "virtual hit" and use that:
							intersectingLine = findIntersectingLineOfSignificanceForHitBox(intersects, {leptonId: lepton.leptonId, x, y, direction: lepton.direction}, leptonHitBox);
						}			
					}
				// Reflect, but don't move.
				return { leptonId: lepton.leptonId, x: lepton.x, y: lepton.y, direction: LeptonDirection[lepton.direction.reflection(intersectingLine.direction.axis.label)]}	
			}
			return { leptonId: lepton.leptonId, x, y, direction: lepton.direction };
		});

		this.setState({ 
			leptons: newLeptons,
		});

		return dying;
	}

	update = () => {
		const { gameState, playerX, playerY, currentDirection, lines, leptons, dyingLeptons, drainAnimationRow } = this.state;
		const { drawMode, paused } = this.props;
		if (paused) {
			// no updates should occur
			return;
		}

		if (gameState === GameState.LEPTON_DYING) {
			const now = new Date().getTime();
			if (this.leptonDeathAnimationStartedAt === 0) {
				 //console.log(`Lepton death animation starting at ${now}`);
				this.leptonDeathAnimationStartedAt = now;
			} else {
				if (now >= this.leptonDeathAnimationStartedAt + LEPTON_DEATH_ANIMATION_LENGTH_MS) {
					 //console.log(`Lepton death animation complete`);

					 this.leptonDeathAnimationStartedAt = 0;
					 this.setState( {
						 gameState: GameState.LEPTON_DIED,
						 dyingLeptons: [],
						 leptonDeathTick: 0,
					 });
					if (leptons.length === 0) {
						this.props.onLeptonsKilled(dyingLeptons.length);
						// Killed the last one!
						this.props.onFinalLeptonKilled();
						console.log(`Level transition signalled. Going to DRAINING`);
						this.setState({ gameState: GameState.DRAINING});
						return
					} else {
						this.props.onLeptonsKilled(dyingLeptons.length);
						// Clock can restart
						this.props.requestCounterPauseState(true);
					}
				} else {
					this.setState(({ leptonDeathTick }) => ({ leptonDeathTick: leptonDeathTick + 1}))
				}
			}
			return;
		}


		if (gameState === GameState.PLAYER_DYING) {
			const now = new Date().getTime();
			if (this.playerDeathAnimationStartedAt === 0) {
				console.log(`Player death animation starting at ${now}`);
				this.playerDeathAnimationStartedAt = now;
				// Also tell the parent to stop the clock:
				this.props.requestCounterPauseState(false);
			} else {
				if (now >= this.playerDeathAnimationStartedAt + PLAYER_DEATH_ANIMATION_LENGTH_MS) {
					console.log(`Player death animation complete`);
					this.playerDeathAnimationStartedAt = 0;
					this.playerDied();
				}
			}
			return;
		}

		if (gameState === GameState.FILLING) {
			// console.time('filling');
			const { polygonToFill, partiallyFilledArea: oldPFA, newFilledPolygons } = this.state;
			if (!polygonToFill) {
				console.log(`No poly to fill`);
				return;
			}
			const { partiallyFilledArea, complete } = floodFillNextTick(polygonToFill, oldPFA);


			if (complete) {
				this.setState({
					filledPolygons: newFilledPolygons as Array<FrenzyFilledPolygon>,
					partiallyFilledArea,
					gameState: GameState.FILL_COMPLETED
				});
			} else {
				this.setState({
					partiallyFilledArea,
				});
			}
			// console.timeEnd('filling');

			return; 
		}

		if (gameState === GameState.FILL_REQUIRED) {
			const { fillLines, fillSpeed, filledPolygons }  = this.state;
			const { newLines, newFilledPolygons, filledPercentage, filledPolygon } = this.fillArea(fillLines, fillSpeed, lines, filledPolygons);
			this.setState({
				gameState: GameState.FILLING,
				lines: newLines,
				filledPercentage,
				newFilledPolygons,
				polygonToFill: filledPolygon,
				partiallyFilledArea: undefined,
			});
			// Also tell the parent to stop the clock:
			this.props.requestCounterPauseState(false);
			return;
		}

		if (gameState === GameState.FILL_COMPLETED) {
			const [dyingLeptons, leptons]: [LeptonState[], LeptonState[]] = partition(this.state.leptons, (l: LeptonState) => {
				if (this.state.polygonToFill) {
					return isInsideComplexPolygon(l.x, l.y, this.state.polygonToFill.polygonLines)
				}
				return false;
			});

			if (dyingLeptons.length > 0) {
				console.log(`Captured! ${describeLeptons(dyingLeptons)} -> inside ${describePolygon(this.state.polygonToFill)}`);
				this.setState({
					gameState: GameState.LEPTON_DYING,
					dyingLeptons,
					leptons,
				});
				return;
			}

			if (this.props.onCompletionPercentageChanged(100 - this.state.filledPercentage)) {
				console.log(`Level transition signalled. Going to DRAINING`);
				this.setState({ gameState: GameState.DRAINING});
			} else {
				this.setState({
					gameState: GameState.IDLE,
				});
			}
			

			// Clock can restart
			this.props.requestCounterPauseState(true);
			return;
		}


		if (gameState === GameState.DRAINING) {

			const points = this.state.filledPolygons.reduce((acc: number, poly:FrenzyFilledPolygon) => {
				const worth = Math.floor((2 / (poly.drawMode || 2)) * poly.area / 16);
				// console.log(`poly: ${describePolygon(poly)} drawn in: ${poly.drawMode} is worth: ${worth}`)
				return acc + worth;
			}, 0)
	
			const firstInterestingRow = topmostPointForPolygons(this.state.filledPolygons);
			const newDrainAnimationRow = (firstInterestingRow < MAX_Y) ? firstInterestingRow : MAX_Y;
			const pointsPerRow = Math.floor(points / (MAX_Y - firstInterestingRow));
	
	

			if (drainAnimationRow === -1) {
				console.log(`Draining for points: ${points}`);
				this.setState({drainAnimationRow: newDrainAnimationRow});
			} else if (drainAnimationRow === MAX_Y) {		
				console.log(`Draining complete`);

				this.setState({gameState: GameState.DRAIN_COMPLETED});
				if (this.state.finalizingScore) {
					this.props.onFinalScoreCalculated(pointsPerRow);
				} else {
					this.props.onLevelTransitionFinished(pointsPerRow);
				}		
			} else {
				
				this.props.onScoreIncrement(pointsPerRow);
				this.setState({drainAnimationRow: drainAnimationRow + 1 });
			}

			return;	
		}

		if (gameState === GameState.DRAIN_COMPLETED) {
			this.setState({gameState: GameState.IDLE});
			return;
		}

		const playerDying = this.animateLeptons();
		if (playerDying) {
			this.setState({ 
				gameState:GameState.PLAYER_DYING
			});
			return;
		}

		const allDesiredDirections = this.keyDownDirection();
		if (allDesiredDirections.length === 0) return;
		const desiredDirection = allDesiredDirections[0];

		if (this.props.drawMode === DrawMode.NOT_DRAWING) {
			const {playerX: newPlayerX, playerY: newPlayerY, chosenDirection } = GameArea.moveOnLine(allDesiredDirections, playerX, playerY, currentDirection, lines, drawMode);
			if (chosenDirection) {
				this.setState({playerX: newPlayerX, playerY: newPlayerY, currentDirection: chosenDirection });
			}
		} else {
			// We are in a draw mode. First see if it is being cancelled:
			if (this.drawIsBeingCancelled(desiredDirection)) {
				this.props.onDrawFinished();
				return;
			}

			// If we haven't yet officially started drawing, we must
			// set up a new draw
			let newX, newY;
			if (this.state.gameState !== GameState.DRAWING) {
				if (canPlayerMoveOnExistingLine(playerX, playerY, desiredDirection, lines)) {
					// They are actually attempting to "draw" on top of an existing line. Bail
					console.log(`Draw on existing line cancels draw mode`);
					this.props.onDrawFinished();
					return;
				}

				[newX, newY] = this.startDraw(desiredDirection);

			} else {
				// We're continuing to draw
				[newX, newY] = this.updateDraw(desiredDirection);
			}

			// If the result of moving 1px is that we've hit an existing line, 
			// the draw is complete.
			if (this.isOnLine(newX, newY)) {
				// console.log(`Found ${newX}, ${newY} to be on a line, final segment is ${describeLine(this.state.currentSegment)}`)
				this.finishDraw();
				this.props.onDrawFinished();
				return;
			}

			if (drawMode === DrawMode.FAST) {
				// Move ANOTHER pixel in the desired direction, and retest
				[newX, newY] = this.updateDraw(desiredDirection);
			}

			// If the result of moving is that we've hit an existing line, 
			// the draw is complete.
			if (this.isOnLine(newX, newY)) {
				this.finishDraw();
				this.props.onDrawFinished();
			}
		}
	}

	renderCounting = (filledPolygons: FrenzyPolygon[]) => {
		// The "drain" animation algorithm:
		// Find the top of the highest filled polygon(s) (i.e. smallest Y)
		// Iterate "downwards" (increasing Y), creating a new polygon for each
		// new one we find. This will be black with a transparent stroke. 
		// Replace the old polygon's "top edge" with the new Y value and continue
		// until we've hit HEIGHT
		return null;
	}


	render() {
		const { dyingLeptons, leptonDeathTick, gameState, leptons, lines, currentDrawingLines, currentSegment, playerX, playerY } = this.state;
		const { level, scale } = this.props;

		const activeLines = currentSegment ? addNonEmptyLines(currentDrawingLines, [currentSegment]) : currentDrawingLines;

		const tempFilledPolys = [
			fillPolygon(
				linesToPolygon(
				buildLinesToJoinPoints(
					[
						[100, MAX_Y],
						[100,50],
						[150,50],
						[150,MAX_Y]
					], true)
				), DrawMode.SLOW
			)];


		if (gameState === GameState.PLAYER_DYING) {
			console.log(`player dying ...`);
		}

		// console.log(`dying leptons to render: ${describeLeptons(dyingLeptons)}`)
		// console.log(`gameState: ${JSON.stringify(gameState, null, 2)}`)
		return (
			<GamePanel className="GamePanel" scale={this.props.scale}>
				<Display
					scale={this.props.scale}
					height={HEIGHT}
					width={WIDTH}
					borderLines={BORDER_LINES}
					completedLines={lines}
					activeLines={activeLines}
					filledPolygons={this.state.filledPolygons}
					// filledPolygons={tempFilledPolys}
					partiallyFilledArea={this.state.partiallyFilledArea}
					drainAnimationRow={this.state.drainAnimationRow}
					// drainAnimationRow={133}
				/>
				{
					leptons.map( (leptonState) =>
						<Lepton key={`lepton-${leptonState.leptonId}`} leptonId={leptonState.leptonId} scale={scale} x={leptonState.x * scale} y={leptonState.y * scale} direction={leptonState.direction} length={LEPTON_LENGTH} speed={level.leptonSpeed}/>
					)
				}
				{
					dyingLeptons.map( (leptonState) =>
						<Lepton key={`dying-lepton-${leptonState.leptonId}`} leptonId={leptonState.leptonId} scale={scale} x={leptonState.x * scale} y={leptonState.y * scale} direction={leptonState.direction} length={LEPTON_LENGTH} speed={level.leptonSpeed} dying leptonDeathTick={leptonDeathTick}/>
					)
				}
				<Player scale={scale} x={playerX * scale} y={playerY * scale} moveable dying={gameState === GameState.PLAYER_DYING} />
			</GamePanel>
		)
	};
}

export default GameArea;
